import { keepPreviousData } from '@tanstack/react-query';
import { flexRender } from '@tanstack/react-table';
import { addMinutes, format, parse } from 'date-fns';
import { EditIcon, EyeIcon, FilterIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { BackOfficePermissionsWrapper } from '../../../components/pages/backoffice/permissions-wrapper.jsx';
import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import { DataTableColumnHeader } from '../../../components/table/data-table-column-header.jsx';
import { DataTableFooter } from '../../../components/table/data-table-footer.jsx';
import { DataTableToolbar } from '../../../components/table/data-table-toolbar.jsx';
import Heading from '../../../components/typography/heading.jsx';
import { Button } from '../../../components/ui/button.jsx';
import { DebouncedInput } from '../../../components/ui/input-debounced.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select.jsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { useTable } from '../../../hooks/use-table.jsx';
import { TABLE_FILTER_LIKE } from '../../../utils/constants.js';
import { filterIgnoreAccents } from '../../../utils/filters.js';

const TABLE_COLUMNS = [
  {
    title: 'Data de Criação',
    accessorKey: 'createdTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Criação" />
    ),
    cell: ({ row }) => {
      const dateString = row.getValue('createdTime');

      if (!dateString) {
        return <div>---</div>;
      }

      try {
        const inputFormat = 'yyyy-MM-dd HH:mm:ss:SSS';
        const dateObject = parse(dateString, inputFormat, new Date());

        return <div>{format(dateObject, 'dd-MM-yyyy HH:mm:ss')}</div>;
      } catch (error) {
        console.error('Failed to parse date:', dateString, error);

        return <div>---</div>;
      }
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    title: 'Data de Expiração',
    accessorKey: 'expiredTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Expiração" />
    ),
    cell: ({ row }) => {
      return (
        <div>
          {format(
            addMinutes(row.original.createdTime, 15),
            'dd-MM-yyyy HH:mm:ss',
          )}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    title: 'Ação',
    accessorKey: 'action',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Ação" />
    ),
    cell: ({ row }) => (
      <div className="w-48">
        {row.getValue('action') === 'create'
          ? 'Criação de Conta'
          : row.getValue('action') === 'update'
            ? 'Atualização de Conta'
            : 'Renovação de Consentimento'}
      </div>
    ),
    enableSorting: true,
    enableHiding: true,
  },
  {
    title: 'Nome',
    accessorKey: 'fullName',
    size: 256,
    header: ({ column }) => (
      <DataTableColumnHeader
        className="min-w-64 max-w-[400px] xl:min-w-[350px]"
        column={column}
        title="Nome"
      />
    ),
    cell: ({ row }) => (
      <Link to={row.original.id}>
        <div className="min-w-64 max-w-[400px] text-balance underline xl:min-w-[350px]">
          {row.original.firstName} {row.original.lastName}
        </div>
      </Link>
    ),
    enableSorting: false,
    enableHiding: false,
    filterFn: (row, id, value) => {
      const fullName = `${row.original.firstName} ${row.original.lastName}`;
      return filterIgnoreAccents(fullName, value);
    },
    filterType: TABLE_FILTER_LIKE.BOTH,
  },
  {
    title: 'Nº Identificação',
    accessorKey: 'currentCustomerIdentificationDoc',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nº Identificação" />
    ),
    cell: ({ row }) => (
      <div>{row.getValue('currentCustomerIdentificationDoc')}</div>
    ),
    enableSorting: true,
    enableHiding: true,
  },
  {
    title: 'Email',
    accessorKey: 'email',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Email" />
    ),
    cell: ({ row }) => <div>{row.getValue('email')}</div>,
    enableSorting: true,
    enableHiding: true,
  },
  {
    title: 'Nº Telemóvel',
    accessorKey: 'phone',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nº Telemóvel" />
    ),
    cell: ({ row }) => <div>{row.getValue('phone')}</div>,
    enableSorting: true,
    enableHiding: true,
  },
  {
    title: 'Localização',
    accessorKey: 'location',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Localização" />
    ),
    cell: ({ row }) => <div>{row.getValue('location')}</div>,
    enableSorting: true,
    enableHiding: true,
  },
  {
    title: 'Distrito',
    accessorKey: 'district',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Distrito" />
    ),
    cell: ({ row }) => <div>{row.getValue('district')}</div>,
    enableSorting: true,
    enableHiding: true,
    filterFn: (row, id, value) => {
      return filterIgnoreAccents(row.getValue(id), value);
    },
  },
  {
    title: 'Morada',
    accessorKey: 'address',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Morada" />
    ),
    cell: ({ row }) => <div>{row.getValue('address')}</div>,
    enableSorting: true,
    enableHiding: true,
  },
  {
    title: 'Código Postal',
    accessorKey: 'zipCode',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Código Postal" />
    ),
    cell: ({ row }) => <div>{row.getValue('zipCode')}</div>,
    enableSorting: false,
    enableHiding: true,
  },
  {
    title: 'Operador',
    accessorKey: 'staff',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Operador" />
    ),
    cell: ({ row }) => <div>{row.getValue('staff')}</div>,
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <div className="flex flex-row space-x-2">
        <Link to={row.original.id}>
          <Button
            variant="outline"
            className="h-auto p-2"
            aria-label="Ver consentimento pendente"
          >
            <EyeIcon />
          </Button>
        </Link>

        <BackOfficePermissionsWrapper>
          <Link to={`${row.original.id}/edit`}>
            <Button
              variant="outline"
              className="h-auto p-2"
              aria-label="Editar consentimento pendente"
            >
              <EditIcon />
            </Button>
          </Link>
        </BackOfficePermissionsWrapper>
      </div>
    ),
  },
];

export function CustomersPendingConsentList() {
  const {
    table,
    query: queryCustomersPendingConsent,
    filters,
    selectColumnFilter,
    setSelectColumnFilter,
  } = useTable(
    TABLE_COLUMNS,
    {
      queryKey: ['customers_consent-requests'],
      queryFn: async () => {
        const { data: customersConsetRequests } = await msCatalystApi.get(
          `/customers/consent-requests/paginate?${filters}`,
        );

        return customersConsetRequests.data;
      },
      refetchOnWindowFocus: true,
      placeholderData: keepPreviousData,
      select: (data) => {
        return {
          ...data,
          rows: data.rows.map((item) => {
            const treatedCustomer = JSON.parse(item.customer);

            return {
              ...item,
              ...treatedCustomer,
              id: item.ROWID,
              createdTime: item.CREATEDTIME,
            };
          }),
        };
      },
    },
    {
      selectColumnFilter: 'currentCustomerIdentificationDoc',
      sorting: [
        {
          id: 'createdTime',
          desc: true,
        },
      ],
    },
  );

  return (
    <>
      <div className="flex flex-col items-start justify-start gap-y-6 pb-6 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
        <Heading>Clientes Pendentes de Consentimento</Heading>
      </div>

      {queryCustomersPendingConsent.isLoading ||
      queryCustomersPendingConsent.isPending ? (
        <div className="flex h-[70dvh] items-center justify-center">
          <Loading />
        </div>
      ) : queryCustomersPendingConsent.isError ? (
        <Error />
      ) : (
        <div className="space-y-4">
          <DataTableToolbar
            table={table}
            showRefetchOption={true}
            refetchFunc={() => queryCustomersPendingConsent.refetch()}
          >
            <Select
              value={selectColumnFilter}
              onValueChange={(e) => {
                setSelectColumnFilter(e);
              }}
            >
              <SelectTrigger className="w-auto">
                <FilterIcon className="mr-2 size-4" />
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent>
                {[
                  { label: 'Nome', value: 'fullName' },
                  { label: 'Nº Identificação', value: 'cc' },
                  { label: 'Email', value: 'email' },
                  { label: 'Nº Telemóvel', value: 'phone' },
                ].map((item, i) => (
                  <SelectItem key={i} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DebouncedInput
              placeholder={`Pesquisar...`}
              value={
                table.getColumn(selectColumnFilter)?.getFilterValue() ?? ''
              }
              onChange={(val) =>
                table.getColumn(selectColumnFilter)?.setFilterValue(val)
              }
              className="h-10 md:w-[255px]"
            />
          </DataTableToolbar>

          <div className="rounded-md border border-muted-foreground/35">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} colSpan={header.colSpan}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {queryCustomersPendingConsent.isRefetching ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Loading />
                    </TableCell>
                  </TableRow>
                ) : queryCustomersPendingConsent.isRefetchError ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Error />
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      {table.getState().columnFilters.length
                        ? 'Não foram encontrados resultados.'
                        : 'Não existem quaisquer Clientes na base de dados.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <DataTableFooter hide={{ selectedRows: true }} table={table} />
        </div>
      )}
    </>
  );
}
